import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EmptyState, SearchInput, SortableTh, TablePagination, TableRowActions } from '@/shared/components'
import { useEtudiants, useDeleteEtudiant } from '../hooks/use-etudiants'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'

interface EtudiantListProps {
  adminMode?: boolean
}

export function EtudiantList({ adminMode }: EtudiantListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: etudiants, isLoading } = useEtudiants()
  const deleteEtudiant = useDeleteEtudiant()
  const [searchQuery, setSearchQuery] = useState('')

  const searchFiltered = useMemo(() => {
    if (!etudiants) return []
    if (!searchQuery.trim()) return etudiants
    const q = searchQuery.toLowerCase()
    return etudiants.filter(
      (e) =>
        e.nom.toLowerCase().includes(q) ||
        e.prenom.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q),
    )
  }, [etudiants, searchQuery])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(searchFiltered, 'nom')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, nom: string, prenom: string) => {
    setDeleteDialog({ id, label: `${prenom} ${nom}` })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteEtudiant.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!etudiants || etudiants.length === 0) {
    return <EmptyState message={t('student.list.noStudents', 'Aucun étudiant. Créez votre premier étudiant !')} />
  }

  return (
    <>
      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('student.list.searchPlaceholder', 'Rechercher par nom, prénom ou email...')}
          className="max-w-md"
        />
      </div>

      {searchFiltered.length === 0 ? (
        <EmptyState message={t('student.list.noResults', 'Aucun étudiant ne correspond à votre recherche.')} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
                <th className="w-12 px-4 py-3" />
                <SortableTh label={t('student.list.columns.firstName', 'Prénom')} sortKey="prenom" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
                <SortableTh label={t('student.list.columns.lastName', 'Nom')} sortKey="nom" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
                <SortableTh label={t('student.list.columns.email', 'Email')} sortKey="email" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
                <SortableTh label={t('student.list.columns.login', 'Login')} sortKey="login" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
                <SortableTh label={t('student.list.columns.phone', 'Téléphone')} sortKey="telephone" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {paginatedData.map((etudiant) => (
                <tr
                  key={etudiant.id}
                  onClick={adminMode ? undefined : () => navigate(`/etudiants/${etudiant.id}`)}
                  className={cn('transition-colors hover:bg-neutral-50', !adminMode && 'cursor-pointer')}
                >
                  <td className="px-4 py-3">
                    {etudiant.photoUrl ? (
                      <img src={etudiant.photoUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-xs font-bold text-white">
                        {etudiant.prenom?.[0]}{etudiant.nom?.[0]}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-neutral-900">{etudiant.prenom}</td>
                  <td className="px-4 py-3 text-sm font-medium text-neutral-900">{etudiant.nom}</td>
                  <td className="px-4 py-3 text-sm text-neutral-500">{etudiant.email}</td>
                  <td className="px-4 py-3 text-sm text-neutral-500">{etudiant.login}</td>
                  <td className="px-4 py-3 text-sm text-neutral-500">{etudiant.telephone || '—'}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <TableRowActions
                      editTo={adminMode ? undefined : `/etudiants/${etudiant.id}`}
                      onDelete={() => handleDelete(etudiant.id, etudiant.nom, etudiant.prenom)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer l'étudiant "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
